import { ComponentProps, FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Maybe, User } from '@treams/treams-network';

import { LockIcon } from '../../icons';
import { Avatar } from '../Avatar';
import { Button } from '../button/Button';
import { ContextMenu } from '../ContextMenu';
import { FlexGap } from '../FlexGap';
import { IconWrapper } from '../Icon';
import { Paragraph } from '../Text';
import { ShareModal } from './ShareModal';

type Props = {
	owner: User;
	collaborators: User[];
	subject?: Maybe<User> | undefined;

	shareMutation: ComponentProps<typeof ShareModal>['shareMutation'];
	unshareMutation?: ComponentProps<typeof ShareModal>['unshareMutation'];
	entityType: 'conversation' | 'note' | 'oneOnOne';
	buttonProps?: ComponentProps<typeof Button>;
	shareModalProps?: Omit<
		ComponentProps<typeof ShareModal>,
		'owner' | 'collaborators' | 'subject' | 'shareMutation' | 'unshareMutation' | 'entityType'
	>;
	canShare?: boolean;
	/**
	 * Whether to render the collaborator indicator (avatar stack or "private" lock + label).
	 * Defaults to `true`. Set to `false` when the indicator is rendered elsewhere.
	 */
	showIndicator?: boolean;
	/**
	 * Whether to render the share button trigger (and its share modal).
	 * Defaults to `true`. Set to `false` when the trigger is rendered elsewhere.
	 */
	showTrigger?: boolean;
};

export const ShareButton: FC<Props> = ({
	owner,
	collaborators,
	subject,
	shareMutation,
	unshareMutation,
	entityType,
	buttonProps,
	shareModalProps,
	canShare = true,
	showIndicator = true,
	showTrigger = true,
}) => {
	const { t } = useTranslation();

	const privateEntityDescription = useMemo(() => {
		switch (entityType) {
			case 'conversation':
				return t('note.sharing.private.label.conversation');
			case 'oneOnOne':
				return t('note.sharing.private.label.oneOnOne');
			case 'note':
			default:
				return t('note.sharing.private.label.note');
		}
	}, [entityType, t]);

	const contextWidth = 440;

	return (
		<FlexGap alignItems="center" gap={16}>
			{showIndicator && collaborators.length > 1 && (
				<Avatar.Stack users={collaborators} shownAvatarAmount={3} />
			)}
			{showIndicator && collaborators.length < 2 && (
				<FlexGap minWidth="fit-content">
					<IconWrapper>
						<LockIcon />
					</IconWrapper>
					<Paragraph lineClamp={1}>{privateEntityDescription}</Paragraph>
				</FlexGap>
			)}
			{showTrigger && (
				<ContextMenu.Card
					overflow="visible"
					height="fit-content"
					title={t('note.sharing.modal.title')}
					trigger={
						<Button
							height={38}
							title={
								canShare
									? t('note.sharing.tigger.button.title')
									: t('note.sharing.viewSettings.button.title')
							}
							type="tertiary"
							{...buttonProps}
						/>
					}
					minWidth={contextWidth}
					hasBackButton={false}
					targetProps={{ horizontalAlign: 'right' }}
				>
					<ShareModal
						owner={owner}
						collaborators={collaborators}
						subject={subject}
						shareMutation={shareMutation}
						unshareMutation={unshareMutation}
						contextWidth={contextWidth}
						canShare={canShare}
						entityType={entityType}
						{...shareModalProps}
					/>
				</ContextMenu.Card>
			)}
		</FlexGap>
	);
};
